import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import WhatsAppCTA from '../components/WhatsAppCTA';

const Pricing = () => {
  const services = [
    {
      id: 1,
      title: 'MAINTENANCE DETAIL',
      image: 'https://images.unsplash.com/photo-1607860108855-64acf2078ed9?w=800&q=80',
      description: 'Keep your vehicle looking great with our Maintenance Detail every 4-8 weeks after your Full Detail service. A Full Detail is required before enrolling in our maintenance program.',
      price: 'Prices from €60',
      note: '*20% off on all bigger packages after being on our maintenance program',
      link: '/maintenance-detail'
    },
    {
      id: 2,
      title: 'FULL DETAIL',
      image: 'https://images.unsplash.com/photo-1520340356584-f9917d1eea6f?w=800&q=80',
      description: 'The perfect initial service before moving on to our Maintenance Detail. Our Full Detail covers all aspects of your vehicle, including a deep clean of interior and exterior.',
      price: 'Prices from €150',
      link: '/full-detail'
    },
    {
      id: 3,
      title: 'ULTIMATE DETAIL',
      image: 'https://images.unsplash.com/photo-1601362840469-51e4d8d58785?w=800&q=80',
      description: 'The perfect service to make your vehicle feel like it\'s truly yours! Our Ultimate Detail covers every aspect, including a deep clean of the interior (roof lining and seat belts), exterior (clay paintwork and gloss enhancement polish), and engine bay detail.',
      price: 'Prices from €280',
      link: '/ultimate-detail'
    },
    {
      id: 4,
      title: 'ENGINE BAY DETAIL',
      image: 'https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?w=800&q=80',
      description: 'A deep cleanse of the engine bay, restoring a new factory finish.',
      price: 'Prices from €80',
      link: '/booking'
    },
    {
      id: 5,
      title: 'HEADLIGHT RESTORATION',
      image: 'https://images.unsplash.com/photo-1605559424843-9e4c228bf1c2?w=800&q=80',
      description: 'Our Professional Headlight Restoration Services can renew headlights affected by oxidation, enhancing both their clarity and appearance. This not only improves light quality but also prevents potential NCT failure, ensuring safer driving conditions.',
      price: 'Prices from €80',
      link: '/booking'
    },
    {
      id: 6,
      title: 'PAINT CORRECTIONS',
      image: 'https://images.unsplash.com/photo-1520340356584-f9917d1eea6f?w=800&q=80',
      description: 'Removing defects and imperfections from the surface of the paint. Increase gloss and clarity of paintwork.',
      pricing: [
        'Gloss Enhancement Polish - from €180',
        '1 Step Correction - from €300',
        '2 Step Correction - from €500'
      ],
      link: '/paint-correction'
    },
    {
      id: 7,
      title: 'CERAMIC COATING',
      image: 'https://images.unsplash.com/photo-1618843479313-40f8afb4b4d8?w=800&q=80',
      description: 'A permanent layer of protection with different variations of durability.',
      pricing: [
        '6 months/1 year - from €200',
        '2/4 year - from €350',
        '5/7 year - from €500'
      ],
      link: '/ceramic-coating'
    }
  ];

  return (
    <div className="min-h-screen bg-black">
      <Helmet>
        <title>Car Detailing Prices Tralee | From €60 | Kingdom Auto Detailing Kerry</title>
        <meta name="description" content="Car detailing prices in Tralee: Maintenance Detail €60, Full Detail €150, Ultimate Detail €280. Ceramic coating from €200. Best value car valeting near Tralee, Kerry. Book today!" />
        <meta name="keywords" content="car detailing prices Tralee, car valet cost Tralee, how much car detailing Tralee, cheap car detailing Tralee, car wash prices Tralee, ceramic coating cost Tralee, paint correction prices Tralee Kerry, mobile valeting prices Tralee" />
        <link rel="canonical" href="https://kingdomautodetailing.ie/pricing" />
      </Helmet>

      {/* Hero Section */}
      <section className="py-20 px-4 bg-gradient-to-b from-zinc-900 to-black">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-6xl md:text-8xl font-bold text-white mb-4 tracking-wider">
            CAR DETAILING PRICES
          </h1>
          <p className="text-xl text-gray-400 mt-4">Professional car valeting in Tralee, Kerry from €60</p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 px-4 bg-black">
        <div className="max-w-7xl mx-auto space-y-16">
          {services.map((service, index) => (
            <div 
              key={service.id}
              className={`grid md:grid-cols-2 gap-8 items-center ${
                index % 2 === 1 ? 'md:flex-row-reverse' : ''
              }`}
            >
              {/* Image */}
              <div className={`${index % 2 === 1 ? 'md:order-2' : ''}`}>
                <div className="relative overflow-hidden rounded-3xl group">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-[400px] object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                </div>
              </div>

              {/* Content */}
              <div className={`${index % 2 === 1 ? 'md:order-1' : ''}`}>
                <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 tracking-wider">
                  {service.title}
                </h2>
                <p className="text-gray-300 text-lg mb-6 leading-relaxed">
                  {service.description}
                </p>
                
                {service.price && (
                  <p className="text-2xl font-bold text-amber-500 mb-4">
                    {service.price}
                  </p>
                )}
                
                {service.pricing && (
                  <div className="space-y-2 mb-6">
                    {service.pricing.map((price, idx) => (
                      <p key={idx} className="text-xl text-amber-500 font-semibold">
                        {price}
                      </p>
                    ))}
                  </div>
                )}

                {service.note && (
                  <p className="text-blue-400 text-sm mb-6 italic">
                    {service.note}
                  </p>
                )}

                <div className="h-px bg-gray-700 my-6"></div>

                <Link
                  to={service.link}
                  className="inline-block bg-blue-600 hover:bg-blue-700 text-white font-bold px-8 py-4 rounded-lg transition-colors text-lg"
                >
                  MORE ABOUT THIS SERVICE
                </Link>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* WhatsApp CTA */}
      <WhatsAppCTA />

      {/* Additional Info Section */}
      <section className="py-16 px-4 bg-zinc-900">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-3xl p-8 md:p-12 text-center">
            <h3 className="text-3xl font-bold text-white mb-6">Ready to Get Started?</h3>
            <p className="text-blue-100 text-lg mb-8 max-w-2xl mx-auto">
              Book your detailing service today and experience the Kingdom Auto Detailing difference. 
              We're committed to making your vehicle look its absolute best.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/booking"
                className="inline-block bg-amber-500 hover:bg-amber-600 text-zinc-900 font-bold px-8 py-4 rounded-full transition-colors text-lg"
              >
                Book Now
              </Link>
              <a
                href="tel:0896536680"
                className="inline-block bg-white hover:bg-gray-100 text-blue-600 font-bold px-8 py-4 rounded-full transition-colors text-lg"
              >
                Call Us: 089 653 6680
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Pricing;
